import { useMemo } from 'react';
import { X, MapPin, Store, Hash, Download, Package } from 'lucide-react';
import {
    getProductBreakdownByCity,
    getProductBreakdownByStore,
    getProductBreakdownByPincode,
} from '../../utils/salesMixGeo';
import { formatCurrency, formatNumber, formatPercentage } from '../../utils/formatters';
import { exportToCSV } from '../../utils/export';
import styles from './ProductBreakdownDrawer.module.css';

/**
 * ProductBreakdownDrawer Component
 * Shows product consumption breakdown by City, Store, and Pincode
 * Desktop: Right slide-in drawer
 * Mobile: Bottom sheet
 */
function ProductBreakdownDrawer({
    isOpen,
    onClose,
    product,
    filteredOrders,
    totalRevenue,
    totalVolume,
}) {
    // Compute breakdowns
    const cityBreakdown = useMemo(() => {
        if (!product || !filteredOrders) return [];
        return getProductBreakdownByCity(filteredOrders, product.id);
    }, [product, filteredOrders]);

    const storeBreakdown = useMemo(() => {
        if (!product || !filteredOrders) return [];
        return getProductBreakdownByStore(filteredOrders, product.id);
    }, [product, filteredOrders]);

    const pincodeBreakdown = useMemo(() => {
        if (!product || !filteredOrders) return [];
        return getProductBreakdownByPincode(filteredOrders, product.id);
    }, [product, filteredOrders]);

    // Product share in current scope
    const productShare = useMemo(() => {
        if (!product || totalRevenue <= 0) return 0;
        return (product.revenue / totalRevenue) * 100;
    }, [product, totalRevenue]);

    // Rank badge helper
    const getRankClass = (index) => {
        if (index === 0) return styles.gold;
        if (index === 1) return styles.silver;
        if (index === 2) return styles.bronze;
        return styles.default;
    };

    // Export handlers
    const handleExportCities = () => {
        const data = cityBreakdown.map((item, idx) => ({
            Rank: idx + 1,
            City: item.name,
            Revenue: item.revenue,
            Volume: item.volume,
            Orders: item.orders,
            'Share %': item.revenueShare.toFixed(1),
        }));
        exportToCSV(data, `${product?.name || 'product'}_cities_breakdown`);
    };

    const handleExportStores = () => {
        const data = storeBreakdown.map((item, idx) => ({
            Rank: idx + 1,
            Store: item.name,
            Revenue: item.revenue,
            Volume: item.volume,
            Orders: item.orders,
            'Share %': item.revenueShare.toFixed(1),
        }));
        exportToCSV(data, `${product?.name || 'product'}_stores_breakdown`);
    };

    const handleExportPincodes = () => {
        const data = pincodeBreakdown.map((item, idx) => ({
            Rank: idx + 1,
            Pincode: item.code,
            Revenue: item.revenue,
            Volume: item.volume,
            Orders: item.orders,
            'COD %': item.codPercent.toFixed(1),
            'Share %': item.revenueShare.toFixed(1),
        }));
        exportToCSV(data, `${product?.name || 'product'}_pincodes_breakdown`);
    };

    // Handle overlay click
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!product) return null;

    return (
        <>
            {/* Overlay */}
            <div
                className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
                onClick={handleOverlayClick}
            />

            {/* Drawer */}
            <div className={`${styles.drawer} ${isOpen ? styles.open : ''}`}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerTitle}>
                        <Package size={20} />
                        Product Consumption Breakdown
                    </div>
                    <button className={styles.closeButton} onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className={styles.content}>
                    {/* Product Summary */}
                    <div className={styles.productSummary}>
                        <div className={styles.productName}>{product.name}</div>
                        <div className={styles.productCategory}>{product.categoryName}</div>
                        <div className={styles.summaryStats}>
                            <div className={styles.summaryStat}>
                                <span className={styles.summaryStatLabel}>Revenue</span>
                                <span className={styles.summaryStatValue}>
                                    {formatCurrency(product.revenue, { compact: true })}
                                </span>
                            </div>
                            <div className={styles.summaryStat}>
                                <span className={styles.summaryStatLabel}>Volume</span>
                                <span className={styles.summaryStatValue}>
                                    {formatNumber(product.volume)}
                                </span>
                            </div>
                            <div className={styles.summaryStat}>
                                <span className={styles.summaryStatLabel}>Share</span>
                                <span className={styles.summaryStatValue}>
                                    {formatPercentage(productShare)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Top Cities */}
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionTitle}>
                                <MapPin />
                                Top Cities
                            </div>
                            <button className={styles.exportButton} onClick={handleExportCities}>
                                <Download size={12} />
                                CSV
                            </button>
                        </div>
                        {cityBreakdown.length > 0 ? (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>City</th>
                                        <th>Revenue</th>
                                        <th>Vol</th>
                                        <th>Share</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cityBreakdown.map((item, idx) => (
                                        <tr key={item.id}>
                                            <td>
                                                <span className={`${styles.rankBadge} ${getRankClass(idx)}`}>
                                                    {idx + 1}
                                                </span>
                                            </td>
                                            <td>{item.name}</td>
                                            <td>{formatCurrency(item.revenue, { compact: true })}</td>
                                            <td>{formatNumber(item.volume)}</td>
                                            <td>{formatPercentage(item.revenueShare)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.emptyState}>No city data available</div>
                        )}
                    </div>

                    {/* Top Stores */}
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionTitle}>
                                <Store />
                                Top Stores
                            </div>
                            <button className={styles.exportButton} onClick={handleExportStores}>
                                <Download size={12} />
                                CSV
                            </button>
                        </div>
                        {storeBreakdown.length > 0 ? (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Store</th>
                                        <th>Revenue</th>
                                        <th>Vol</th>
                                        <th>Share</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {storeBreakdown.map((item, idx) => (
                                        <tr key={item.id}>
                                            <td>
                                                <span className={`${styles.rankBadge} ${getRankClass(idx)}`}>
                                                    {idx + 1}
                                                </span>
                                            </td>
                                            <td>{item.name}</td>
                                            <td>{formatCurrency(item.revenue, { compact: true })}</td>
                                            <td>{formatNumber(item.volume)}</td>
                                            <td>{formatPercentage(item.revenueShare)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.emptyState}>No store data available</div>
                        )}
                    </div>

                    {/* Top Pincodes */}
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionTitle}>
                                <Hash />
                                Top Pincodes
                            </div>
                            <button className={styles.exportButton} onClick={handleExportPincodes}>
                                <Download size={12} />
                                CSV
                            </button>
                        </div>
                        {pincodeBreakdown.length > 0 ? (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Pincode</th>
                                        <th>Revenue</th>
                                        <th>COD%</th>
                                        <th>Share</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pincodeBreakdown.map((item, idx) => (
                                        <tr key={item.code}>
                                            <td>
                                                <span className={`${styles.rankBadge} ${getRankClass(idx)}`}>
                                                    {idx + 1}
                                                </span>
                                            </td>
                                            <td>{item.code}</td>
                                            <td>{formatCurrency(item.revenue, { compact: true })}</td>
                                            <td>
                                                <span className={styles.codBadge}>
                                                    {item.codPercent.toFixed(0)}%
                                                </span>
                                            </td>
                                            <td>{formatPercentage(item.revenueShare)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className={styles.emptyState}>No pincode data available</div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default ProductBreakdownDrawer;
